import React, { useContext, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import ScienceIcon from '@mui/icons-material/Science'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'
import BigModal from '../../../components/Modals/BigModal'
import SmallModal from '../../../components/Modals/SmallModal'
import QRCodePopup from '../../../components/QRCode/QRCodePopup'
import EmptyState from '../../../components/EmptyState/EmptyState'
import AnDateField from '../../../components/DateTime/DateField'
import { SectionHeader } from '../../fleetEquipment/vehicles/VehicleComponents/SectionKit'

// GHS hazard classes (freeSolo — anything else can be typed in)
const HAZARD_OPTIONS = [
  'Flammable',
  'Corrosive',
  'Acute toxicity',
  'Irritant',
  'Health hazard',
  'Serious health hazard',
  'Oxidising',
  'Explosive',
  'Gas under pressure',
  'Hazardous to the environment',
]

const reviewStatus = (sheet) => {
  if (!sheet.reviewDate) return null
  const review = moment(sheet.reviewDate)
  if (review.isBefore(moment(), 'day'))
    return {
      label: `Review overdue (${review.format('DD/MM/YY')})`,
      color: 'error',
    }
  if (review.diff(moment(), 'days') <= 60)
    return { label: `Review due ${review.format('DD/MM/YY')}`, color: 'warning' }
  return { label: `Review ${review.format('DD/MM/YY')}`, color: 'default' }
}

const SheetForm = ({ open, onClose, base, existing, onSaved }) => {
  const { setToast } = useContext(GlobalContext)
  const [name, setName] = useState('')
  const [manufacturer, setManufacturer] = useState('')
  const [hazards, setHazards] = useState([])
  const [reviewDate, setReviewDate] = useState(null)
  const [notes, setNotes] = useState('')
  const [scope, setScope] = useState('base')
  const [newFile, setNewFile] = useState(null)
  const [saving, setSaving] = useState(false)
  const newFileInput = useRef()

  useEffect(() => {
    if (!open) return
    setName(existing?.name ?? '')
    setManufacturer(existing?.manufacturer ?? '')
    setHazards(existing?.hazards ?? [])
    setReviewDate(existing?.reviewDate ?? null)
    setNotes(existing?.notes ?? '')
    setScope(existing ? (existing.baseLocation ? 'base' : 'all') : 'base')
    setNewFile(null)
  }, [open, existing])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      let docLink = existing?.docLink
      if (newFile) {
        const urlRes = await API.post('/gets3uploadUrl', {
          folder: 'coshh',
          extension: 'pdf',
        })
        if (urlRes.status !== 200) throw new Error('upload url')
        const putRes = await API.put(urlRes.data.url, newFile, {
          headers: { 'Content-Type': 'application/pdf' },
        })
        if (putRes.status !== 200) throw new Error('s3 put')
        docLink = urlRes.data.key
      }
      const payload = {
        name,
        manufacturer,
        hazards,
        reviewDate,
        notes,
        docLink,
        baseLocation: scope === 'base' ? base._id : null,
      }
      const res = existing
        ? await API.put(`/coshh-sheets/${existing._id}`, payload)
        : await API.post('/coshh-sheets', payload)
      if (res.status !== 200) throw new Error('save')
      setToast({
        message: existing ? 'Data sheet updated' : 'Data sheet uploaded',
      })
      onSaved()
    } catch (err) {
      console.error(err)
      setToast({
        message: 'Unable to save data sheet. Please try again.',
        severity: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <BigModal autoWidth open={open} onClose={onClose}>
      <Stack
        gap={2}
        component="form"
        onSubmit={handleSubmit}
        minWidth={{ sm: 480 }}
      >
        <Typography variant="title2">
          {existing ? 'Edit COSHH Data Sheet' : 'Add a COSHH Data Sheet'}
        </Typography>
        <TextField
          label="Substance / Product Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          fullWidth
        />
        <TextField
          label="Manufacturer / Supplier"
          value={manufacturer}
          onChange={(e) => setManufacturer(e.target.value)}
          fullWidth
        />
        <Autocomplete
          multiple
          freeSolo
          options={HAZARD_OPTIONS}
          value={hazards}
          onChange={(e, value) => setHazards(value)}
          renderInput={(props) => (
            <TextField {...props} label="Hazard Classes" />
          )}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <AnDateField
            fullWidth
            date={reviewDate}
            onChange={(newDate) => setReviewDate(newDate)}
            label="Review Date"
          />
          <TextField
            select
            fullWidth
            label="Relevant To"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
          >
            <MenuItem value="base">{base.name}</MenuItem>
            <MenuItem value="all">All locations</MenuItem>
          </TextField>
        </Stack>
        <TextField
          label="Notes (storage, first aid, spill response...)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          minRows={2}
          fullWidth
        />
        <Button
          variant="outlined"
          color="anMainBlue"
          onClick={() => newFileInput.current.click()}
          fullWidth
        >
          {newFile
            ? newFile.name
            : existing
              ? 'Replace Data Sheet PDF (optional)'
              : 'Select Data Sheet PDF'}
        </Button>
        <input
          ref={newFileInput}
          type="file"
          style={{ display: 'none' }}
          accept={'application/pdf'}
          onChange={(e) => setNewFile(e.target.files[0])}
        />
        <Button
          disabled={saving || (!existing && !newFile)}
          type="submit"
          color="anGreen"
          fullWidth
          variant="contained"
        >
          {saving ? 'Saving...' : 'Submit'}
        </Button>
      </Stack>
    </BigModal>
  )
}

const CoshhSection = ({ base, canManage, onCountChange }) => {
  const { setToast } = useContext(GlobalContext)
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [sheets, setSheets] = useState([])
  const [formOpen, setFormOpen] = useState(false)
  const [editSheet, setEditSheet] = useState(null)
  const [qrSheet, setQrSheet] = useState(null)
  const [deleteSheet, setDeleteSheet] = useState(null)

  const loadSheets = () => {
    setLoading(true)
    setFetchError(false)
    API.get(`/coshh-sheets?baseLocation=${base._id}`)
      .then((res) => {
        if (res.status === 200) {
          setSheets(res.data)
          onCountChange?.(res.data.length)
        } else {
          setFetchError(true)
        }
      })
      .catch((e) => {
        console.error(e)
        setFetchError(true)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadSheets()
  }, [base._id])

  const viewSheet = (sheet) => {
    API.post('/gets3downloadurl?inlineView=true', { key: sheet.docLink }).then(
      (res) => {
        if (res.status === 200) {
          window.open(res.data, '_blank')
        } else {
          setToast({
            message: 'Unable to open data sheet. Please try again.',
            severity: 'error',
          })
        }
      },
    )
  }

  const confirmDelete = () => {
    API.delete(`/coshh-sheets/${deleteSheet._id}`).then((res) => {
      if (res.status === 200) {
        setToast({ message: 'Data sheet deleted' })
        setDeleteSheet(null)
        loadSheets()
      } else {
        setToast({
          message: 'Unable to delete data sheet. Please try again.',
          severity: 'error',
        })
      }
    })
  }

  return (
    <Stack spacing={2}>
      <SectionHeader
        title="COSHH Data Sheets"
        caption="Safety data sheets for substances stored or used at this base. Print a QR label for your COSHH store — scanning it opens the sheet, no login needed."
        action={
          canManage && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setEditSheet(null)
                setFormOpen(true)
              }}
            >
              Add Data Sheet
            </Button>
          )
        }
      />

      {loading && <LinearProgress />}
      {fetchError && (
        <Alert severity="error" variant="filled">
          Unable to load COSHH data sheets. Please try again or contact AmbuNet
          support.
        </Alert>
      )}

      {!loading && !fetchError && sheets.length === 0 && (
        <EmptyState
          icon={ScienceIcon}
          title="No COSHH data sheets yet"
          description="Upload the manufacturer safety data sheet for each substance kept at this base, then print its QR code and stick it in your COSHH store."
          actions={
            canManage
              ? [
                  {
                    label: 'Add a data sheet',
                    onClick: () => {
                      setEditSheet(null)
                      setFormOpen(true)
                    },
                  },
                ]
              : undefined
          }
        />
      )}

      {!loading && !fetchError && sheets.length > 0 && (
        <Stack divider={<Divider sx={{ opacity: 0.6 }} />}>
          {sheets.map((sheet) => {
            const review = reviewStatus(sheet)
            return (
              <Stack
                key={sheet._id}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                py={1.25}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <Box minWidth={0} flex={1}>
                  <Typography fontWeight={700}>{sheet.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[
                      sheet.manufacturer,
                      sheet.baseLocation
                        ? sheet.baseLocation.name || base.name
                        : 'All locations',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                  {sheet.hazards?.length > 0 && (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      flexWrap="wrap"
                      useFlexGap
                      mt={0.5}
                    >
                      {sheet.hazards.map((hazard) => (
                        <Chip
                          key={hazard}
                          size="small"
                          label={hazard}
                          variant="outlined"
                          sx={{ borderRadius: 1 }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {review && (
                    <Chip
                      size="small"
                      label={review.label}
                      color={review.color}
                      variant={
                        review.color === 'default' ? 'outlined' : 'filled'
                      }
                      sx={{ borderRadius: 1, fontWeight: 700 }}
                    />
                  )}
                  <Tooltip title="View data sheet">
                    <IconButton size="small" onClick={() => viewSheet(sheet)}>
                      <PictureAsPdfIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="QR code for your COSHH store">
                    <IconButton size="small" onClick={() => setQrSheet(sheet)}>
                      <QrCode2Icon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {canManage && (
                    <>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditSheet(sheet)
                            setFormOpen(true)
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => setDeleteSheet(sheet)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Stack>
              </Stack>
            )
          })}
        </Stack>
      )}

      <SheetForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        base={base}
        existing={editSheet}
        onSaved={() => {
          setFormOpen(false)
          loadSheets()
        }}
      />

      <QRCodePopup
        open={!!qrSheet}
        onClose={() => setQrSheet(null)}
        url={
          qrSheet &&
          `${window.location.origin}/coshh/${qrSheet.publicToken}`
        }
        title={qrSheet?.name}
        imageLabel={qrSheet?.name}
        scanHint="Scan to view COSHH data sheet"
      />

      <SmallModal
        open={!!deleteSheet}
        onClose={() => setDeleteSheet(null)}
        style={{ width: 'min(92vw, 360px)', padding: 16 }}
      >
        <Stack spacing={1.5}>
          <Typography fontWeight={700}>
            Delete "{deleteSheet?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The data sheet PDF will be removed and any printed QR codes for it
            will stop working.
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              color="success"
              fullWidth
              onClick={confirmDelete}
            >
              Yes - Delete
            </Button>
            <Button
              variant="contained"
              color="error"
              fullWidth
              onClick={() => setDeleteSheet(null)}
            >
              No - Cancel
            </Button>
          </Stack>
        </Stack>
      </SmallModal>
    </Stack>
  )
}

export default CoshhSection
